import React, { useState, useEffect } from "react";
import {
  Circle,
  ArchiveIcon,
  PackagePlusIcon,
  ShieldCheckIcon,
  CheckCheckIcon,
  PlusIcon,
} from "lucide-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useNotification } from "../../../hooks";
import { useGetCollections, useGetTestCasesByEndpoint } from "../../../useQuery";

const AddTestCaseModal = ({ open, onClose, suiteId, onAddTestCase, addingToSuite }) => {
  const notify = useNotification();
  const [collections, setCollections] = useState([]);
  const [expandedCollections, setExpandedCollections] = useState({});
  const [expandedEndpoints, setExpandedEndpoints] = useState({});
  const [testCasesByEndpoint, setTestCasesByEndpoint] = useState({});

  const [selectedCases, setSelectedCases] = useState({});

  const { mutate: fetchCollections, isPending: loadingCollections } = useGetCollections({
    onSuccess: (data) => setCollections(data || []),
    onError: () => notify("Error fetching collections", "error"),
  });

  const { mutate: fetchTestCases, isPending: loadingTestCases } = useGetTestCasesByEndpoint({
    onSuccess: (data, variables) => {
      setTestCasesByEndpoint((prev) => ({
        ...prev,
        [variables.endpointId]: data || [],
      }));
    },
    onError: () => notify("Error fetching test cases", "error"),
  });

  useEffect(() => {
    if (open) {
      fetchCollections();
      setExpandedCollections({});
      setExpandedEndpoints({});
      setTestCasesByEndpoint({});
      setSelectedCases({});
    }
  }, [open]);

  const handleCollectionChange = (collectionId) => (event, isExpanded) => {
    setExpandedCollections((prev) => ({ ...prev, [collectionId]: isExpanded }));
    if (isExpanded) {
      const collection = collections.find((c) => c.id === collectionId);
      collection?.endpoints?.forEach((endpoint) => {
        if (!testCasesByEndpoint[endpoint.id]) {
          fetchTestCases({ endpointId: endpoint.id });
        }
      });
    }
  };

  const handleEndpointChange = (endpointId) => (event, isExpanded) => {
    setExpandedEndpoints((prev) => ({ ...prev, [endpointId]: isExpanded }));
  };

  const toggleCase = (testCase, endpointId, collectionId) => {
    setSelectedCases((prev) => {
      const next = { ...prev };
      if (next[testCase.id]) {
        delete next[testCase.id];
      } else {
        next[testCase.id] = { testCase, endpointId, collectionId };
      }
      return next;
    });
  };

  const toggleSelectAllForEndpoint = (endpointId, collectionId, e) => {
    e.stopPropagation();
    const testCases = testCasesByEndpoint[endpointId] || [];
    const allSelected = testCases.every((tc) => selectedCases[tc.id]);

    setSelectedCases((prev) => {
      const next = { ...prev };
      if (allSelected) {
        testCases.forEach((tc) => delete next[tc.id]);
      } else {
        testCases.forEach((tc) => {
          next[tc.id] = { testCase: tc, endpointId, collectionId };
        });
      }
      return next;
    });
  };

  const isEndpointAllSelected = (endpointId) => {
    const tcs = testCasesByEndpoint[endpointId] || [];
    return tcs.length > 0 && tcs.every((tc) => selectedCases[tc.id]);
  };

  const isEndpointPartialSelected = (endpointId) => {
    const tcs = testCasesByEndpoint[endpointId] || [];
    return tcs.some((tc) => selectedCases[tc.id]) && !isEndpointAllSelected(endpointId);
  };

  const selectedCount = Object.keys(selectedCases).length;

  const handleAdd = () => {
    Object.values(selectedCases).forEach(({ testCase, endpointId, collectionId }) => {
      onAddTestCase(testCase, endpointId, collectionId);
    });
    setSelectedCases({});
  };

  const getMethodStyles = (method) => {
    switch (method?.toUpperCase()) {
      case "GET":
        return { backgroundColor: "#f0f9ff", color: "#065f46", borderColor: "#10b981" };
      case "POST":
        return { backgroundColor: "#eff6ff", color: "#1e40af", borderColor: "#3b82f6" };
      case "PUT":
        return { backgroundColor: "#fff7ed", color: "#c2410c", borderColor: "#f97316" };
      case "DELETE":
        return { backgroundColor: "#fef2f2", color: "#dc2626", borderColor: "#ef4444" };
      case "PATCH":
        return { backgroundColor: "#faf5ff", color: "#7c3aed", borderColor: "#8b5cf6" };
      default:
        return { backgroundColor: "#f9fafb", color: "#374151", borderColor: "#6b7280" };
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <p className="font-apple text-[20px] font-medium">Add Test Cases To Suite</p>
        <PackagePlusIcon className="ml-4" size={24} />
      </DialogTitle>

      <DialogContent
        sx={{
          padding: 0,
          backgroundColor: "#ffffff",
          overflow: "hidden",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          className="font-apple"
          sx={{
            flex: 1,
            overflowY: "auto",
            padding: "16px",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": { backgroundColor: "#f1f1f1" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#c1c1c1", borderRadius: "4px" },
            "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#a1a1a1" },
          }}
        >
          {loadingCollections ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <Typography variant="body2" color="text.secondary">
                Loading collections...
              </Typography>
            </Box>
          ) : collections.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={12}>
              <Typography variant="body2" color="text.secondary">
                No collections found
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: "100%" }}>
              {collections.map((collection) => (
                <Accordion
                  key={collection.id}
                  expanded={expandedCollections[collection.id] || false}
                  onChange={handleCollectionChange(collection.id)}
                  sx={{
                    mb: 2,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": { margin: "0 0 16px 0" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: "#fafafa",
                      borderBottom: "1px solid #e0e0e0",
                      "&:hover": { backgroundColor: "rgba(25,118,210,0.08)" },
                      "& .MuiAccordionSummary-content": { alignItems: "center" },
                    }}
                  >
                    <ArchiveIcon size={18} />
                    <p className="font-apple ml-4 text-sm font-medium">{collection.name}</p>
                    <Chip
                      label={`${collection.endpoints?.length || 0} endpoints`}
                      size="small"
                      sx={{ ml: 2 }}
                      variant="outlined"
                    />
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 0 }}>
                    {collection.endpoints?.map((endpoint) => {
                      const methodStyles = getMethodStyles(endpoint.method);
                      const testCases = testCasesByEndpoint[endpoint.id] || [];
                      const allSelected = isEndpointAllSelected(endpoint.id);
                      const partialSelected = isEndpointPartialSelected(endpoint.id);

                      return (
                        <Accordion
                          key={endpoint.id}
                          expanded={expandedEndpoints[endpoint.id] || false}
                          onChange={handleEndpointChange(endpoint.id)}
                          sx={{
                            "&:before": { display: "none" },
                            "&.Mui-expanded": { margin: 0 },
                            boxShadow: "none",
                            borderTop: "1px solid #e0e0e0",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              backgroundColor: "#f9f9f9",
                              minHeight: "48px",
                              "&.Mui-expanded": { minHeight: "48px" },
                              "& .MuiAccordionSummary-content": {
                                alignItems: "center",
                                margin: "8px 0",
                                mr: 1,
                              },
                            }}
                          >
                            <Circle size={4} className="text-gray-400 fill-current mr-3" />
                            <Chip
                              label={endpoint.method}
                              size="small"
                              sx={{
                                ...methodStyles,
                                fontSize: "10px",
                                height: "20px",
                                minWidth: "60px",
                                mr: 2,
                              }}
                            />
                            <p className="font-apple text-sm font-medium flex-1">{endpoint.path}</p>

                            {testCases.length > 0 && (
                              <label
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1.5 mr-2 cursor-pointer select-none group"
                              >
                                <input
                                  type="checkbox"
                                  checked={allSelected}
                                  ref={(el) => {
                                    if (el) el.indeterminate = partialSelected;
                                  }}
                                  onChange={(e) =>
                                    toggleSelectAllForEndpoint(endpoint.id, collection.id, e)
                                  }
                                  className="w-4 h-4 accent-blue-600 cursor-pointer"
                                />
                                <span className="text-xs text-gray-500 group-hover:text-blue-600 transition-colors flex items-center gap-1">
                                  <CheckCheckIcon size={12} />
                                  Select All
                                </span>
                              </label>
                            )}
                          </AccordionSummary>

                          <AccordionDetails sx={{ pl: 6, pr: 2, py: 2 }}>
                            {loadingTestCases ? (
                              <Typography variant="caption" color="text.secondary">
                                Loading test cases...
                              </Typography>
                            ) : testCases.length > 0 ? (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {testCases.map((testCase) => {
                                  const isChecked = !!selectedCases[testCase.id];
                                  return (
                                    <label
                                      key={testCase.id}
                                      className={`flex items-center gap-3 p-3 rounded border cursor-pointer transition-all duration-150
                                        ${
                                          isChecked
                                            ? "bg-blue-50 border-blue-200"
                                            : "bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                                        }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() =>
                                          toggleCase(testCase, endpoint.id, collection.id)
                                        }
                                        className="w-4 h-4 accent-blue-600 cursor-pointer shrink-0"
                                      />
                                      <ShieldCheckIcon
                                        size={14}
                                        className="text-green-500 shrink-0"
                                      />
                                      <div className="min-w-0 flex-1">
                                        <p className="font-apple text-sm text-gray-800">
                                          {testCase.testCase}
                                        </p>
                                        <p className="text-[11px] text-blue-500 mt-0.5">
                                          {testCase.testType}
                                        </p>
                                      </div>
                                    </label>
                                  );
                                })}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                                No test cases found
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <span className="font-apple text-sm">
          {selectedCount > 0 ? (
            <span className="text-blue-600 font-medium">
              {selectedCount} test case{selectedCount !== 1 ? "s" : ""} selected
            </span>
          ) : (
            <span className="text-gray-400">Select test cases to add</span>
          )}
        </span>

        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 font-apple text-sm text-white bg-red-500 hover:bg-red-600 rounded transition-all duration-200 cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={selectedCount === 0 || addingToSuite}
            className={`flex items-center gap-2 px-5 py-2 font-apple text-sm text-white rounded transition-all duration-200
              ${
                selectedCount === 0 || addingToSuite
                  ? "bg-blue-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 cursor-pointer"
              }`}
          >
            <PlusIcon size={14} />
            {addingToSuite ? "Adding..." : `Add${selectedCount > 0 ? ` (${selectedCount})` : ""}`}
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default AddTestCaseModal;
